import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px;
    color: ${({ theme }) => theme.text};
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 10px;
`;

const LastUpdated = styled.p`
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 40px;
    font-style: italic;
`;

const Section = styled.section`
    margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: ${({ theme }) => theme.primary};
`;

const Paragraph = styled.p`
    line-height: 1.7;
    margin-bottom: 15px;
`;

const List = styled.ul`
    margin-left: 20px;
    margin-bottom: 15px;
    
    li {
        line-height: 1.7;
        margin-bottom: 8px;
    }
`;

const PrivacyPolicy = () => {
    return (
        <Container>
            <Title>Privacy Policy</Title>
            <LastUpdated>Last Updated: February 9, 2026</LastUpdated>

            <Section>
                <Paragraph>
                    PropNinja ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at propninja.ai.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Information We Collect</SectionTitle>
                <Paragraph>We collect information you provide directly to us, including:</Paragraph>
                <List>
                    <li><strong>Account Information:</strong> When you create an account, we collect your email address and password (encrypted).</li>
                    <li><strong>Payment Information:</strong> Payment processing is handled by Stripe. We do not store your credit card details.</li>
                    <li><strong>Usage Data:</strong> Information about how you interact with our services, including property searches and analysis requests.</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>Google Sign-In</SectionTitle>
                <Paragraph>
                    If you choose to sign in with Google, we receive your email address and basic profile information from Google. We use this solely for authentication purposes and do not access your Google contacts, calendar, or other Google services.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>reCAPTCHA</SectionTitle>
                <Paragraph>
                    This site is protected by Google reCAPTCHA to prevent spam and abuse. Use of reCAPTCHA is subject to Google's <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>How We Use Your Information</SectionTitle>
                <List>
                    <li>To provide, maintain, and improve our services</li>
                    <li>To process your transactions and manage your subscription</li>
                    <li>To send you technical notices and support messages</li>
                    <li>To respond to your comments and questions</li>
                    <li>To protect against fraudulent or unauthorized activity</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>Data Sharing</SectionTitle>
                <Paragraph>We do not sell your personal information. We may share information with:</Paragraph>
                <List>
                    <li><strong>Service Providers:</strong> Third parties that help us operate our services (Stripe for payments, Firebase for authentication, Google Cloud for hosting)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>Data Security</SectionTitle>
                <Paragraph>
                    We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Your Rights</SectionTitle>
                <Paragraph>You have the right to:</Paragraph>
                <List>
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data</li>
                    <li>Cancel your subscription at any time</li>
                </List>
            </Section>

            <Section>
                <SectionTitle>Cookies</SectionTitle>
                <Paragraph>
                    We use essential cookies for authentication and session management. We also use analytics to understand how our service is used.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Changes to This Policy</SectionTitle>
                <Paragraph>
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Contact Us</SectionTitle>
                <Paragraph>
                    If you have questions about this Privacy Policy, please contact us at support@propninja.ai.
                </Paragraph>
            </Section>
        </Container>
    );
};

export default PrivacyPolicy;
